/*
 * @Author: Ryan
 * @Date: 2025-12-29
 * @Description: How to use section for AI Voice Cloning landing page
 */
import Image from 'next/image';
import React from 'react';

interface StepData {
  number: number;
  title: string;
  description: string;
}

const steps: StepData[] = [
  {
    number: 1,
    title: 'Upload a Voice Sample',
    description:
      'Start by recording or uploading a clear audio or video file in MP3, WAV, M4A, MP4, or MOV, with a recommended length of 10 seconds to 3 minutes. A short clip is enough to get started, but longer recordings deliver more natural results.',
  },
  {
    number: 2,
    title: 'Generate Your AI Voice Clone',
    description:
      'Our AI instantly analyzes the sample and creates a lifelike voice profile. Adjust tone, pitch, or style to match your needs — from professional narrations to fun, expressive voices.',
  },
  {
    number: 3,
    title: 'Enter Your Text',
    description:
      'Type or paste any script, and your cloned voice will read it out with natural intonation. You can adjust style, speed, or emotion as needed.',
  },
  {
    number: 4,
    title: 'Export Your Audio',
    description:
      'Download the audio in WAV format and add it directly to your video as needed.',
  },
];

const StepItem: React.FC<StepData> = ({ number, title, description }) => (
  <div className="flex gap-4">
    {/* 步骤编号 */}
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center text-white font-semibold text-sm">
      {number}
    </div>
    {/* 步骤内容 */}
    <div className="flex flex-col gap-2">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          How to Clone a Voice with FalcoCut AI
        </h2>

        {/* 内容区域 */}
        <div className="flex flex-col pad:flex-row gap-8 pad:gap-12 sm:gap-16 items-center">
          {/* 左侧图片 */}
          <div className="w-full pad:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[#F8F9FC]">
              <Image
                src="https://assets.falcocut.ai/ai-voice-cloning/how-to-clone-voice.webp"
                alt="FalcoCut AI Voice Cloning Interface"
                width={560}
                height={480}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* 右侧步骤 */}
          <div className="w-full pad:w-1/2 flex flex-col gap-6 pad:gap-8">
            {steps.map((step) => (
              <StepItem key={step.number} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
